import { Card, CardContent, CardDescription, CardHeader, CardTitle, Page } from '@rammetec/design-system'
import { useAvailableModules } from '@syscolect/application'
import type { ModuleInstance } from '@syscolect/domain'
import type { AppModule } from '@/app/modules/registry'

export interface GenericModulePageProps {
  instance: ModuleInstance
}

/**
 * Fallback page for any slug not yet mapped in `registry.slugToComponent`.
 * Lets the whole nav work end-to-end before the real module page exists
 * (Phase 6/7). Renders the resolved instance + its parameters.
 */
export function GenericModulePage({ instance }: GenericModulePageProps) {
  const modules = useAvailableModules<AppModule>()
  const module = modules.find((m) => m.slug === instance.slug)

  const title = instance.label ?? module?.label ?? instance.slug
  const description = 'Módulo carregado via roteamento por slug (página específica chega na Fase 6/7).'
  const parameterEntries = Object.entries(instance.parameters)

  return (
    <Page name={`console.module.${instance.slug}`} title={title} description={description} productArea="console.modules">
      <Card className="max-w-2xl">
        <CardHeader>
          <CardTitle>Instância carregada</CardTitle>
          <CardDescription>
            <code>id</code>: {instance.id} · <code>slug</code>: {instance.slug}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {parameterEntries.length === 0 ? (
            <p className="text-sm text-fg-muted">Sem parâmetros configurados — esta instância usa apenas o slug.</p>
          ) : (
            <dl className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-2 text-sm">
              {parameterEntries.map(([key, value]) => (
                <div key={key} className="contents">
                  <dt className="font-medium text-fg">{key}</dt>
                  <dd className="text-fg-muted">{formatValue(value)}</dd>
                </div>
              ))}
            </dl>
          )}
        </CardContent>
      </Card>
    </Page>
  )
}

function formatValue(value: unknown): string {
  if (value == null) return '—'
  if (typeof value === 'object') return JSON.stringify(value)
  return String(value)
}
